/* Activity: one ledger across every card, corrections and rejections included.
 *
 * THE WORDS ARE THE ONES THIS SCREEN ALREADY HAD, and the reasons with them. A
 * feed that showed only the good news would be a worse lie than showing
 * nothing: a reversal and an offline stamp that could not be confirmed are the
 * two things that change a balance with nothing else able to say why.
 *
 * THE ARTBOARD'S TWO TILES ARE NOT HERE. "Stamps this year" and "rewards value
 * in riyals": the feed returns the latest fifty entries, so a yearly total
 * would be wrong for anyone past fifty, and no reward has a riyal value.
 *
 * GROUPED THE WAY THE ARTBOARD GROUPS, by when: today, yesterday, earlier this
 * week, earlier.
 */
import { t } from "@damga/kit";

export type Entry = {
  entry_id: string;
  type: string;
  occurred_at: string;
  business_name: string;
  points: number;
  reason_code: string | null;
  actor_role: string | null;
  reward_description: string | null;
  expires_at: string | null;
  rejection_code: string | null;
  reverses_occurred_at: string | null;
  /** A reward the shop took back: the stamp that finished the card was undone. */
  voided?: boolean;
  /** A correction that took a reward with it. */
  voids_reward?: boolean;
};

const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const daysAgo = (iso: string) => Math.round((midnight(new Date()) - midnight(new Date(iso))) / 86_400_000);

function when(iso: string): string {
  const at = new Date(iso);
  const days = daysAgo(iso);
  if (days <= 1) return at.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" });
  if (days < 7) return at.toLocaleDateString("en-GB", { weekday: "long" });
  return at.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function dayPhrase(iso: string): string {
  const days = daysAgo(iso);
  if (days <= 0) return t("time.today").toLowerCase();
  if (days === 1) return t("time.yesterday").toLowerCase();
  const at = new Date(iso);
  if (days < 7) return at.toLocaleDateString("en-GB", { weekday: "long" });
  return at.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

function detail(e: Entry): string | null {
  switch (e.type) {
    case "EARN_REVERSAL":
      return t(
        e.voids_reward
          ? "activity.EARN_REVERSAL.withReward"
          : e.actor_role === "system"
            ? "activity.EARN_REVERSAL.plain"
            : "activity.EARN_REVERSAL.byStaff",
        "en",
        { when: e.reverses_occurred_at ? dayPhrase(e.reverses_occurred_at) : "earlier" },
      );
    case "SYNC_REJECTED": {
      const key =
        e.rejection_code === "E_TOO_SOON"
          ? "activity.SYNC_REJECTED.E_TOO_SOON"
          : e.rejection_code === "E_MEMBER_CAP"
            ? "activity.SYNC_REJECTED.E_MEMBER_CAP"
            : "activity.SYNC_REJECTED.other";
      return `${t(key)} ${t("activity.SYNC_REJECTED.ask")}`;
    }
    case "REWARD_ISSUE":
      // TAKEN BACK IS NOT EARNED (2026-09-18). The feed said "Reward earned" for
      // a reward the shop's undo had voided, so the end-to-end test's customer
      // read two rewards earned and one claimed.
      return e.voided
        ? t("activity.REWARD_ISSUE.voidedDetail", "en", { reward: e.reward_description ?? "" })
        : e.reward_description;
    case "REWARD_USE":
    case "REWARD_USE_REVERSAL":
      return e.reward_description;
    case "IMPORT_OPENING":
      return t("activity.IMPORT_OPENING.detail");
    default:
      return null;
  }
}

function amount(e: Entry): string | null {
  if (e.type === "EARN" || e.type === "IMPORT_OPENING") return `+${e.points}`;
  // A correction that took a reward back gives the card's stamps back too, so its
  // net is a positive number shown in the colour of a loss. The sentence says it.
  if (e.type === "EARN_REVERSAL" && e.voids_reward) return null;
  if (e.type === "EARN_REVERSAL" || e.type === "EXPIRY") return `${e.points}`;
  return null;
}

/* What the dot says. A tick for a stamp, a star for a reward, a turn-back for
 * anything that took something back, and a quiet mark for the rest. */
function dot(type: string): { icon: string; kind?: string } {
  switch (type) {
    case "EARN":
      return { icon: "✓" };
    case "REWARD_ISSUE":
    case "REWARD_USE":
      return { icon: "★", kind: "reward" };
    case "REWARD_USE_REVERSAL":
      return { icon: "★", kind: "quiet" };
    case "IMPORT_OPENING":
      return { icon: "+", kind: "quiet" };
    case "EARN_REVERSAL":
    case "SYNC_REJECTED":
    case "EXPIRY":
      return { icon: "↺", kind: "back" };
    default:
      return { icon: "·", kind: "quiet" };
  }
}

const TAKES_BACK = new Set(["EARN_REVERSAL", "SYNC_REJECTED", "EXPIRY"]);

const HEADINGS: Record<string, Parameters<typeof t>[0]> = {
  EARN: "activity.EARN",
  IMPORT_OPENING: "activity.IMPORT_OPENING",
  REWARD_ISSUE: "activity.REWARD_ISSUE",
  REWARD_USE: "activity.REWARD_USE",
  REWARD_USE_REVERSAL: "activity.REWARD_USE_REVERSAL",
  EXPIRY: "activity.EXPIRY",
  EARN_REVERSAL: "activity.EARN_REVERSAL",
  SYNC_REJECTED: "activity.SYNC_REJECTED",
};

export function ActivityRow({ entry: e, brief = false }: { entry: Entry; brief?: boolean }) {
  const takenBack = e.type === "REWARD_ISSUE" && e.voided;
  const d = takenBack ? dot("EARN_REVERSAL") : dot(e.type);
  const body = brief ? null : detail(e);
  const value = amount(e);
  const heading = takenBack
    ? t("activity.REWARD_ISSUE.voided")
    : HEADINGS[e.type]
      ? t(HEADINGS[e.type])
      : e.type;
  return (
    <li className="row" data-back={TAKES_BACK.has(e.type) || takenBack ? "" : undefined}>
      <span className="dot" data-kind={d.kind} aria-hidden="true">
        {d.icon}
      </span>
      <div className="row-main">
        <p className="row-title">{heading}</p>
        <p className="row-meta">
          {e.business_name} · {when(e.occurred_at)}
        </p>
        {body && <p className="row-detail">{body}</p>}
      </div>
      {value && <span className="row-value">{value}</span>}
    </li>
  );
}

type GroupKey = "activity.group.today" | "activity.group.yesterday" | "activity.group.week" | "activity.group.earlier";

function groupOf(iso: string): GroupKey {
  const days = daysAgo(iso);
  if (days <= 0) return "activity.group.today";
  if (days === 1) return "activity.group.yesterday";
  if (days < 7) return "activity.group.week";
  return "activity.group.earlier";
}

export function Activity({
  entries,
  failed,
  onRetry,
}: {
  entries: Entry[] | null;
  failed: boolean;
  onRetry: () => void;
}) {
  if (failed) {
    return (
      <div className="empty">
        <p className="empty-title">{t("error.generic.title")}</p>
        <p className="empty-body">{t("activity.failed")}</p>
        <button type="button" className="btn btn-secondary" onClick={onRetry}>
          {t("common.retry")}
        </button>
      </div>
    );
  }
  if (entries === null) return <p className="muted">{t("common.loading")}</p>;
  if (entries.length === 0) {
    return (
      <div className="empty">
        <p className="empty-title">{t("activity.empty.title")}</p>
        <p className="empty-body">{t("activity.empty.body")}</p>
      </div>
    );
  }

  const groups: { key: GroupKey; rows: Entry[] }[] = [];
  for (const e of entries) {
    const key = groupOf(e.occurred_at);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.rows.push(e);
    else groups.push({ key, rows: [e] });
  }

  return (
    <>
      {groups.map((g) => (
        <section key={g.key} aria-labelledby={`group-${g.key}`}>
          <h2 className="label section-head" id={`group-${g.key}`}>
            {t(g.key)}
          </h2>
          <ol className="rows">
            {g.rows.map((e) => (
              <ActivityRow key={e.entry_id} entry={e} />
            ))}
          </ol>
        </section>
      ))}
    </>
  );
}
